import { withUppercaseFirstLetter } from '@/common/utils/withUppercaseFirstLetter';

import type {
  DomainGuid,
  GridElement,
  GridElementQuickAccessEvaluatedResult,
  GridElementSection,
  GridElementSectionWithQuickAccessResult,
  QuickAccessEvaluatedResult
} from '../..';
import type { Printable } from '../../domain/Printable';
import type { EvaluatedResult } from '../../evaluation/EvaluatedResult';
import type { CalculationResult } from '../CalculationResult';
import type { GridElementsDetailsByCalculationType } from '../gridElementsDetailsByCalculationType/GridElementsDetails';
import type {
  AcLineLoadFlowDetails,
  BusbarLoadFlowDetails,
  GridElementLoadFlowDetails,
  GridElementLoadFlowDetailsWithResults
} from '../gridElementsDetailsByCalculationType/loadFlow/gridElementLoadFlowDetails.types';
import type {
  GridElementShortCircuitDetails,
  GridElementShortCircuitDetailsWithResults
} from '../gridElementsDetailsByCalculationType/shortCircuit/ShortCircuitDetails';

export function enrichGridElementSectionsWithEvaluatedResults(
  gridElementSections: GridElementSection<GridElement>[],
  gridElementDetails: GridElementsDetailsByCalculationType
): GridElementSectionWithQuickAccessResult[] {
  return gridElementSections.map(section => ({
    ...section,
    gridElements: section.gridElements.map(gridElement => ({
      ...gridElement,
      evaluatedResults: findEvaluatedResults(gridElement, gridElementDetails)
    }))
  }));
}

function findEvaluatedResults(
  gridElement: GridElement,
  gridElementDetails: GridElementsDetailsByCalculationType
): GridElementSectionWithQuickAccessResult['gridElements'][number]['evaluatedResults'] {
  const gridElementResults = GridElementsDetailsWithResults.from(
    gridElementDetails
  ).getResults(gridElement.objectId);

  if (gridElementResults.hasResultsFromBothCalculationTypes) {
    return {
      type: 'NO_EVALUATED_RESULTS_FOR_QUICK_ACCESS'
    };
  }

  return gridElementResults.toQuickAccessResult();
}

class GridElementsDetailsWithResults {
  private constructor(
    private readonly laodFlow: GridElementLoadFlowDetailsWithResults[],
    private readonly shortCircuit: GridElementShortCircuitDetailsWithResults[]
  ) {}

  static from(
    gridElementDetails: GridElementsDetailsByCalculationType
  ): GridElementsDetailsWithResults {
    return new GridElementsDetailsWithResults(
      this.loadFlowWithResults(gridElementDetails.loadFlow),
      this.shortCircuitWithResults(gridElementDetails.shortCircuit)
    );
  }

  private static loadFlowWithResults = (
    gridElement: GridElementLoadFlowDetails[]
  ): GridElementLoadFlowDetailsWithResults[] => {
    return gridElement.filter(
      gridElement => gridElement.objectType !== 'ExternalNetworkInjection'
    ) as GridElementLoadFlowDetailsWithResults[]; // TODO: check why checkTypes was screaming here after removing objects without results. TypeScript is not.
  };

  private static shortCircuitWithResults = (
    gridElement: GridElementShortCircuitDetails[]
  ): GridElementShortCircuitDetailsWithResults[] => {
    return gridElement.filter(
      gridElement => gridElement.objectType !== 'ExternalNetworkInjection'
    ) as GridElementShortCircuitDetailsWithResults[]; // TODO: check why checkTypes was screaming here after removing objects without results. TypeScript is not.
  };

  getResults = (id: DomainGuid) => {
    const loadFlowResults = this.laodFlow.find(
      element => element.id === id
    )?.results;
    const shortCircuitResults = this.shortCircuit.find(
      element => element.id === id
    )?.results;
    return GridElementResults.from(loadFlowResults, shortCircuitResults);
  };
}

class GridElementResults {
  constructor(
    private readonly loadFlowResults:
      | GridElementLoadFlowDetailsWithResults['results']
      | undefined,
    private readonly shortCircuitResults:
      | GridElementShortCircuitDetailsWithResults['results']
      | undefined
  ) {}

  static from(
    loadFlowResults:
      | GridElementLoadFlowDetailsWithResults['results']
      | undefined,
    shortCircuitResults:
      | GridElementShortCircuitDetailsWithResults['results']
      | undefined
  ): GridElementResults {
    return new GridElementResults(loadFlowResults, shortCircuitResults);
  }

  get hasResultsOnlyFromOneCalculationType() {
    return (
      (this.loadFlowResults || this.shortCircuitResults) &&
      !(this.loadFlowResults && this.shortCircuitResults)
    );
  }

  get hasResultsFromBothCalculationTypes() {
    return this.loadFlowResults && this.shortCircuitResults;
  }

  toQuickAccessResult = (): GridElementQuickAccessEvaluatedResult => {
    return QuickAccessResults.fromLoadFlowResults(this.loadFlowResults)
      .concat(
        QuickAccessResults.fromShortCircuitResults(this.shortCircuitResults)
      )
      .toState();
  };
}

class QuickAccessResults {
  constructor(private readonly results: QuickAccessEvaluatedResult[]) {}

  static fromLoadFlowResults = (
    loadFlowResults:
      | GridElementLoadFlowDetailsWithResults['results']
      | undefined
  ): QuickAccessResults => {
    return loadFlowResults
      ? new QuickAccessResults(this._fromLoadFlowResults(loadFlowResults))
      : new QuickAccessResults([]);
  };

  static fromShortCircuitResults = (
    shortCircuitResults:
      | GridElementShortCircuitDetailsWithResults['results']
      | undefined
  ): QuickAccessResults => {
    return shortCircuitResults
      ? new QuickAccessResults(this._fromShortCircuit(shortCircuitResults))
      : new QuickAccessResults([]);
  };

  concat = (results: QuickAccessResults): QuickAccessResults => {
    return new QuickAccessResults(this.results.concat(results.results));
  };

  toState = (): GridElementQuickAccessEvaluatedResult => {
    return this.results.length > 0
      ? {
          type: 'EVALUATED_RESULT_FOR_QUICK_ACCESS',
          evaluatedResults: this.results
        }
      : { type: 'NO_EVALUATED_RESULTS_FOR_QUICK_ACCESS' };
  };

  private static _fromLoadFlowResults(
    loadFlowResults: GridElementLoadFlowDetailsWithResults['results']
  ): QuickAccessEvaluatedResult[] {
    return Object.entries(loadFlowResults)
      .filter(([_, result]) => result.status === 'ATTENTION_REQUIRED')
      .map(([label, result]: [string, EvaluatedResult<Printable>]) => ({
        label: this.getLabel(label),
        value: result.value.toString({ withUnit: true })
      }));
  }

  private static getLabel(label: string): string {
    const acLineSegmentLoadingPercentProperty =
      nameOf<AcLineLoadFlowDetails['results']>('loadingPercent');
    const busbarVoltageDiffPercent =
      nameOf<BusbarLoadFlowDetails['results']>('voltageDiffPercent');
    const loadFlowLabels: Record<string, string> = {
      [acLineSegmentLoadingPercentProperty]: 'Loading',
      [busbarVoltageDiffPercent]: 'Voltage Drop'
    };
    return loadFlowLabels[label] === undefined
      ? withUppercaseFirstLetter(label)
      : loadFlowLabels[label];
  }

  private static _fromShortCircuit(
    shortCircuitResults: GridElementShortCircuitDetailsWithResults['results']
  ): QuickAccessEvaluatedResult[] {
    return Object.entries(shortCircuitResults)
      .filter(
        ([_, calculationResult]) =>
          calculationResult.type === 'CALCULATION_RESULT_AVAILABLE' &&
          calculationResult.result.status === 'ATTENTION_REQUIRED'
      )
      .map(([label, calculationResult]) => ({
        label: withUppercaseFirstLetter(label),
        value: (
          calculationResult as unknown as Extract<
            CalculationResult<EvaluatedResult<Printable>>,
            { type: 'CALCULATION_RESULT_AVAILABLE' }
          >
        ).result.value.toString()
      }));
  }
}

function nameOf<T>(key: keyof T) {
  return key;
}
